import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Alert, AlertColor, Snackbar } from '@mui/material';

export type ToastSeverity = AlertColor;

export interface ToastOptions {
  /** Override the default 6s auto-hide, e.g. longer for a message with an action. */
  autoHideMs?: number;
  /** Rendered in the Alert's action slot (a "View" button, for instance). */
  action?: React.ReactNode;
}

interface ToastContextValue {
  /** Show a transient bottom-centre toast. Defaults to the `info` tone. */
  showToast: (message: string, severity?: ToastSeverity, options?: ToastOptions) => void;
  /** Dismiss the current toast, e.g. from an action button that navigates away. */
  hideToast: () => void;
}

/**
 * Default is a no-op so components stay renderable in tests and in any tree
 * that mounts without the provider; the toast just doesn't appear.
 */
const ToastContext = createContext<ToastContextValue>({
  showToast: () => undefined,
  hideToast: () => undefined,
});

const AUTO_HIDE_MS = 6000;

interface ToastState {
  message: string;
  severity: ToastSeverity;
  autoHideMs: number;
  action?: React.ReactNode;
}

/**
 * One app-wide toast, mounted once at the root.
 *
 * Every page used to keep its own `{ open, message, severity }` state and its
 * own `<Snackbar>` at the bottom of its JSX, each with a slightly different
 * shape. Mounting a single Snackbar here and exposing `showToast` lets pages
 * report outcomes with one call, and gives pages that had no toast at all a
 * way to confirm their mutations.
 */
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<ToastState>({
    message: '',
    severity: 'info',
    autoHideMs: AUTO_HIDE_MS,
  });

  const showToast = useCallback(
    (message: string, severity: ToastSeverity = 'info', options: ToastOptions = {}) => {
      setToast({
        message,
        severity,
        autoHideMs: options.autoHideMs ?? AUTO_HIDE_MS,
        action: options.action,
      });
      setOpen(true);
    },
    [],
  );

  const hideToast = useCallback(() => setOpen(false), []);

  const handleClose = (_event?: unknown, reason?: string) => {
    // Clicking elsewhere on the page should not dismiss an outcome message.
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const value = useMemo(() => ({ showToast, hideToast }), [showToast, hideToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={toast.autoHideMs}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={hideToast} severity={toast.severity} action={toast.action} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => useContext(ToastContext);
